import { isClassicMetricVisible } from './classicMetricsDefaults';

/**
 * Content-generation accuracy metrics (Exact Match, Fuzzy Similarity/MinHash,
 * Dict Match) arrive flattened inside the overview `classic_metrics` rows.
 * These helpers identify those rows so they can be rendered in the dedicated
 * "accuracy" section (absolute numbers) instead of the Classic Metrics chart,
 * and stay hidden entirely for summarization tasks.
 */

const DICT_MATCH_PREFIX = 'dict_match_';

export function isContentGenerationMetricKey(key) {
  if (key == null) return false;
  const k = String(key).toLowerCase();
  return k === 'text_match' || k === 'min_hash' || k === 'dict_match' || k.startsWith(DICT_MATCH_PREFIX);
}

const toFiniteOrNull = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
};

const meanOrNull = (values) => {
  const finite = values.filter((v) => v !== null);
  if (finite.length === 0) return null;
  return finite.reduce((acc, v) => acc + v, 0) / finite.length;
};

/** Backend emits `Dict Match (<field>)` labels; fall back to the row key. */
const dictMatchFieldName = (row) => {
  const label = String(row?.label ?? '');
  const match = label.match(/^Dict Match \((.+)\)$/i);
  if (match) return match[1];
  return String(row?.key ?? '').slice(DICT_MATCH_PREFIX.length);
};

/**
 * Extract the content-generation accuracy metrics from the overview
 * `classic_metrics.metrics` rows, honoring the Configure-modal toggles
 * (exactMatchEnabled / minHashEnabled / dictMatchEnabled).
 *
 * Returns `{ exactMatch, fuzzySimilarity, dictMatch }`; each entry is null
 * when there is no data (or the toggle is off). `dictMatch` carries the
 * per-field rows plus overall teacher/student means (the backend stores
 * Dict Match only per field).
 */
export function extractContentGenerationMetrics(rows, classicConfig) {
  const list = Array.isArray(rows) ? rows : [];

  const scoreRow = (key) => {
    if (!isClassicMetricVisible(key, classicConfig)) return null;
    const row = list.find((r) => String(r?.key ?? '').toLowerCase() === key);
    if (!row) return null;
    const teacher = toFiniteOrNull(row.teacher);
    const student = toFiniteOrNull(row.student);
    if (teacher === null && student === null) return null;
    return { teacher, student };
  };

  let dictMatch = null;
  if (isClassicMetricVisible(DICT_MATCH_PREFIX, classicConfig)) {
    const fields = list
      .filter((r) => String(r?.key ?? '').toLowerCase().startsWith(DICT_MATCH_PREFIX))
      .map((r) => ({
        field: dictMatchFieldName(r),
        teacher: toFiniteOrNull(r.teacher),
        student: toFiniteOrNull(r.student),
      }))
      .filter((r) => r.teacher !== null || r.student !== null)
      .sort((a, b) => a.field.localeCompare(b.field));
    if (fields.length > 0) {
      dictMatch = {
        teacher: meanOrNull(fields.map((f) => f.teacher)),
        student: meanOrNull(fields.map((f) => f.student)),
        fields,
      };
    }
  }

  return {
    exactMatch: scoreRow('text_match'),
    fuzzySimilarity: scoreRow('min_hash'),
    dictMatch,
  };
}

export function hasContentGenerationMetrics(extracted) {
  return Boolean(extracted?.exactMatch || extracted?.fuzzySimilarity || extracted?.dictMatch);
}
